import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card, CardContent } from 'src/components/Card';
import { Skeleton } from 'src/components/Skeleton';
import { useStickyOffset } from 'src/hooks/useStickyOffset';

import { Button } from './Button';
import { Divider } from './Divider';
import { SectionHeader } from './SectionHeader';

interface StickyCardProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  sx?;
  contentSx?;
  stickyOffset?: number;
}

export const StickyCard: React.FC<StickyCardProps> = ({
  title,
  subtitle,
  children,
  actions,
  sx,
  contentSx,
  stickyOffset,
}) => {
  const theme = useTheme();
  const { top: topOffset } = useStickyOffset({ overwriteOffset: stickyOffset });
  const styles = { overflow: 'clip', position: 'relative' };

  return (
    <Card sx={[styles, ...(Array.isArray(sx) ? sx : [sx])]}>
      {title && (
        <Box
          sx={{
            position: 'sticky',
            top: topOffset,
            left: '0px',
            zIndex: '10',
            backdropFilter: 'blur(10px)',
            backgroundColor: `rgba(${theme.vars.palette.background.defaultChannel} / 0.6)`,

            ...theme.applyStyles('dark', {
              backgroundColor: `rgba(${theme.vars.palette.neutral['900Channel']} / 0.6)`,
            }),
          }}
        >
          <SectionHeader title={title} subtitle={subtitle} actions={actions} />
          <Divider sx={{ opacity: 0.7 }} />
        </Box>
      )}
      <CardContent sx={{ '&&': { px: 2, py: 3, ...contentSx } }}>
        {children}
      </CardContent>
    </Card>
  );
};

interface StickyCardSkeletonProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  noActions?: boolean;
  sx?;
}

export const StickyCardSkeleton: React.FC<StickyCardSkeletonProps> = ({
  title,
  children,
  actions,
  noActions = true,
  sx,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'stickyCard' });
  const skeletonTitle = title || (
    <Skeleton variant="text" width={250}></Skeleton>
  );

  const skeletonActions =
    !noActions &&
    (actions || (
      <Skeleton variant="rectangular" sx={{ borderRadius: 1 }}>
        <Button size="small">{t('sampleWidth')}</Button>
      </Skeleton>
    ));

  const skeletonChildren = children || (
    <>
      <Skeleton variant="text" width="100%"></Skeleton>
      <Skeleton variant="text" width="100%"></Skeleton>
      <Skeleton variant="text" width="100%"></Skeleton>
    </>
  );

  return (
    <StickyCard title={skeletonTitle} actions={skeletonActions} sx={sx}>
      {skeletonChildren}
    </StickyCard>
  );
};
